import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { IcoCheck, IcoArrowR, IcoCog, IcoUsers, IcoActivity } from '../bz/icons';
import { PRODUCTS } from '../bz/products';
import usePlatformSummary from '../../hooks/usePlatformSummary';

// Skeleton shimmer for values still loading
const Skel = ({ width = 60 }) => (
  <span
    className="loading-skeleton"
    style={{ display: 'inline-block', width, height: '0.9em', borderRadius: 3, verticalAlign: 'middle' }}
  />
);
Skel.propTypes = { width: PropTypes.number };

const relativeTime = date => {
  if (!date) return null;
  const m = Math.floor((Date.now() - date.getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const firstNameFromEmail = email => {
  if (!email) return null;
  const local = email.split('@')[0];
  const part = local.split(/[._-]/).filter(Boolean)[0] || local;
  return part.charAt(0).toUpperCase() + part.slice(1);
};

const ProductCard = ({ p, metric, loading, onOpen }) => {
  const Icon = p.Icon;
  const isPreview = p.status === 'preview';
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        all: 'unset',
        cursor: 'pointer',
        background: 'var(--bg-elev)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-lg)',
        padding: 22,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-1)',
        transition: 'transform .15s, box-shadow .15s, border-color .15s'
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-3)';
        e.currentTarget.style.borderColor = p.accent;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = 'var(--shadow-1)';
        e.currentTarget.style.borderColor = 'var(--line)';
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: p.accent }} />
      <div className="bz-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: p.accentSoft,
            color: p.accent,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon size={22} />
        </div>
        {isPreview ? (
          <span className="bz-pill bz-pill--predict">Coming soon</span>
        ) : (
          <span className="bz-pill bz-pill--ok">
            <span className="bz-dot" />
            Active
          </span>
        )}
      </div>
      <div>
        <div className="bz-kicker" style={{ color: p.accent }}>
          BugZero
        </div>
        <div className="bz-h2" style={{ marginTop: 2 }}>
          {p.short}
        </div>
        <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 2 }}>
          {p.tag}
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)', color: 'var(--ink-soft)', lineHeight: 1.5 }}>{p.blurb}</p>
      <div
        className="bz-row"
        style={{
          marginTop: 'auto',
          paddingTop: 14,
          borderTop: '1px solid var(--line-soft)',
          justifyContent: 'space-between'
        }}
      >
        <div>
          <div className="bz-kicker">{metric.label}</div>
          <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 500, marginTop: 2 }}>
            {isPreview ? metric.value : loading ? <Skel width={40} /> : metric.value ?? '—'}
          </div>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}>
            {isPreview ? metric.delta : loading ? <Skel width={70} /> : metric.delta}
          </div>
        </div>
        <span
          style={{
            color: p.accent,
            fontWeight: 500,
            fontSize: 'var(--fs-sm)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          Open <IcoArrowR size={13} />
        </span>
      </div>
    </button>
  );
};

ProductCard.propTypes = {
  p: PropTypes.shape({
    accent: PropTypes.string,
    accentSoft: PropTypes.string,
    Icon: PropTypes.elementType,
    short: PropTypes.string,
    tag: PropTypes.string,
    blurb: PropTypes.string,
    status: PropTypes.string
  }).isRequired,
  metric: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    delta: PropTypes.string
  }).isRequired,
  loading: PropTypes.bool,
  onOpen: PropTypes.func
};

// Build the bottom-of-card metric for each product from the live summary
const metricFor = (productId, s) => {
  if (productId === 'prevent') {
    return {
      label: 'Open findings',
      value: s.preventFindings == null ? null : s.preventFindings.toLocaleString(),
      delta: 'last 7 days'
    };
  }
  if (productId === 'plan') {
    return {
      label: 'Active projects',
      value: s.planProjects == null ? null : s.planProjects.toLocaleString(),
      delta: s.planInReview == null ? '' : `${s.planInReview} in review`
    };
  }
  // predict (preview) — static, no backend yet
  return { label: 'Status', value: 'Coming soon', delta: 'Early access' };
};

const PlatformHome = () => {
  const navigate = useNavigate();
  const openSettings = (section = 'organization') => navigate(`/platform/${section}`);
  const s = usePlatformSummary();

  const name = firstNameFromEmail(s.userEmail);

  // Platform-status rows, each backed by a real field (or null → "—")
  const statusRows = [
    {
      key: 'Members',
      target: 'members',
      value:
        s.memberCount == null
          ? null
          : `${s.memberCount} active${s.memberPending ? ` · ${s.memberPending} pending` : ''}`,
      tone: 'ok'
    },
    {
      key: 'SSO',
      target: 'sso',
      value: s.ssoEnabled == null ? null : s.ssoEnabled ? 'Enforced' : 'Not enforced',
      tone: s.ssoEnabled === false ? 'warn' : 'ok'
    },
    {
      key: 'Integrations',
      target: 'integrations',
      value: s.integrationsConnected == null ? null : `${s.integrationsConnected} of ${s.integrationsTotal} connected`,
      tone: 'ok'
    },
    {
      key: 'Data freshness',
      target: null,
      value: s.freshness == null ? null : relativeTime(s.freshness),
      tone: 'ok'
    }
  ];

  return (
    <div
      style={{
        padding: '40px 48px',
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        maxWidth: 1320,
        margin: '0 auto'
      }}
    >
      <div className="bz-row" style={{ justifyContent: 'space-between', alignItems: 'flex-end', gap: 24 }}>
        <div>
          <div className="bz-row" style={{ gap: 10, marginBottom: 8 }}>
            <span className="bz-pill bz-pill--info" style={{ fontSize: 11 }}>
              <span className="bz-dot" /> BugZero Platform
            </span>
            <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
              {s.loading ? (
                <Skel width={120} />
              ) : s.tenant ? (
                <>
                  <strong style={{ color: 'var(--ink)' }}>{s.tenant}</strong> · tenant
                </>
              ) : (
                'tenant'
              )}
            </span>
          </div>
          <h1 className="bz-h1" style={{ fontSize: 'var(--fs-3xl)' }}>
            {name ? `Welcome back, ${name}.` : 'Welcome back.'}
          </h1>
          <p style={{ margin: '6px 0 0', color: 'var(--ink-soft)', fontSize: 'var(--fs-md)', maxWidth: 640 }}>
            One platform, three products — <strong style={{ color: 'var(--bz-blue-1)' }}>Plan</strong>,
            <strong style={{ color: 'var(--bz-orange-1)' }}> Prevent</strong> and
            <strong style={{ color: 'var(--predict)' }}> Predict</strong> — sharing your organization, members,
            integrations and policy.
          </p>
        </div>
        <div className="bz-row" style={{ gap: 8 }}>
          <button type="button" className="bz-btn bz-btn--sm" onClick={() => openSettings()}>
            <IcoCog size={13} /> Platform settings
          </button>
          <button type="button" className="bz-btn bz-btn--sm" onClick={() => openSettings('members')}>
            <IcoUsers size={13} /> Invite teammates
          </button>
        </div>
      </div>

      <div>
        <div className="bz-kicker" style={{ marginBottom: 12 }}>
          Products
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          {PRODUCTS.map(p => (
            <ProductCard
              key={p.id}
              p={p}
              metric={metricFor(p.id, s)}
              loading={s.loading}
              onOpen={() => navigate(p.rootPath)}
            />
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 18 }}>
        {/* Recent activity — no audit/events API exists yet, so show an honest
            empty state instead of fabricated rows. */}
        <div className="bz-card">
          <div
            className="bz-row"
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid var(--line-soft)',
              justifyContent: 'space-between'
            }}
          >
            <h3 className="bz-h3">Recent activity</h3>
          </div>
          <div
            style={{
              padding: '40px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 10,
              color: 'var(--ink-muted)'
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'var(--bg-tan)',
                color: 'var(--ink-muted)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <IcoActivity size={20} />
            </div>
            <div style={{ fontWeight: 500, color: 'var(--ink)' }}>Activity timeline coming soon</div>
            <div style={{ fontSize: 'var(--fs-sm)', maxWidth: 360 }}>
              A unified cross-product event feed will appear here once the platform audit log is available.
            </div>
            <button
              type="button"
              className="bz-btn bz-btn--sm"
              style={{ marginTop: 4 }}
              onClick={() => openSettings('audit')}
            >
              View audit log →
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="bz-card" style={{ padding: 18 }}>
            <div className="bz-row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 className="bz-h3">Platform status</h3>
              <span className="bz-pill bz-pill--ok">
                <IcoCheck size={11} /> Healthy
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {statusRows.map(row => (
                <button
                  key={row.key}
                  type="button"
                  onClick={() => row.target && openSettings(row.target)}
                  style={{
                    all: 'unset',
                    cursor: row.target ? 'pointer' : 'default',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 'var(--fs-sm)',
                    padding: '2px 0'
                  }}
                >
                  <span className="bz-muted">{row.key}</span>
                  <span className="bz-row" style={{ gap: 6 }}>
                    {s.loading ? (
                      <Skel width={90} />
                    ) : row.value == null ? (
                      <span className="bz-muted">—</span>
                    ) : (
                      <>
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: 999,
                            background: row.tone === 'warn' ? 'var(--warn)' : 'var(--ok)'
                          }}
                        />
                        <span>{row.value}</span>
                      </>
                    )}
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="bz-btn bz-btn--sm"
              onClick={() => openSettings()}
              style={{ marginTop: 14, width: '100%', justifyContent: 'center' }}
            >
              Manage platform →
            </button>
          </div>

          <div className="bz-card" style={{ padding: 18 }}>
            <div className="bz-kicker" style={{ marginBottom: 8 }}>
              Shortcuts
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {[
                ['Invite teammate', () => openSettings('members')],
                ['Configure ServiceNow', () => openSettings('integrations')],
                ['View audit log', () => openSettings('audit')],
                ['Manage SSO', () => openSettings('sso')],
                ['Notification rules', () => openSettings('notifications')],
                ['Read the docs', () => navigate('/documentation/user-guides')]
              ].map(([label, action]) => (
                <button
                  key={label}
                  type="button"
                  className="bz-btn bz-btn--sm"
                  style={{ justifyContent: 'flex-start' }}
                  onClick={action}
                >
                  <IcoArrowR size={12} /> {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformHome;
